import {
  Component,
  DestroyRef,
  HostListener,
  OnInit,
  WritableSignal,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { catchError, finalize, of } from 'rxjs';

import { AuthService } from '../../../core/services/auth/auth.service';
import { IconName } from '../../../shared/components/icon/icon-data.generated';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { ProgressSpinnerComponent } from '../../../shared/components/progress-spinner/progress-spinner.component';
import {
  formatDob,
  formatDobNumeric,
  formatLocalDate,
  formatLocalTime,
  formatPhoneNumber,
} from '../../../shared/utils/date-time/date-time.utils';
import { logHttpError } from '../../../shared/utils/logging/http-error-logging.utils';
import { getStatusBadge } from '../../../shared/utils/status-badge/status-badge.utils';
import { RequestDetail, RequestDetailPatient } from '../../models/request-detail';
import { PatientPortalUserRequestNotesService } from '../../services/patient-portal-user-request-notes.service';
import { PatientRecordsSearchStateService } from '../../services/patient-records-search-state.service';
import { PatientPortalUserRequestsService } from '../../services/patient-portal-user-requests.service';
import { getIdentifierAriaLabel, getIdentifierParts } from '../../utils/identifier.utils';
import {
  isDateOfBirthMismatch,
  isMedicalRecordNumberMismatch,
  isNameMismatch,
} from '../../utils/identity-match.utils';
import {
  NOT_AVAILABLE,
  formatPatientAddress,
  formatPatientMrn,
  formatPatientPcp,
  formatPatientPhone,
  getPatientInitials,
  splitDisplayName,
} from '../../../shared/utils/patient-display/patient-display.utils';
import { buildPatientSearchRequest } from '../../utils/patient-search.utils';
import {
  TimelineEvent,
  buildAttemptTimeline,
  buildRequestTimeline,
  formatTimelineTimestamp,
} from '../../utils/request-timeline.utils';
import { AdjustSearch, AdjustSearchCriteria } from '../adjust-search/adjust-search';
import { ConfirmRequestModal } from '../confirm-request-modal/confirm-request-modal';
import { DenyRequestModal } from '../deny-request-modal/deny-request-modal';
import {
  RequestStatusCard,
  RequestStatusSelection,
} from '../request-status-card/request-status-card';

type ActiveModal = 'confirm-link' | 'deny-request' | null;

/** Which search produced the currently displayed patient records. */
type ResultsSource = 'auto' | 'adjusted' | 'handoff';

/**
 * Shown when an action that must be attributed to a real admin user can't find
 * one. These are audit records (notes, assignment, searches), so the action is
 * refused rather than attributed to a made-up id.
 */
const SESSION_ERROR = 'Your session could not be verified. Sign in again and retry.';

const EMPTY_REQUEST_DETAIL: RequestDetail = {
  version: 0,
  phoneNumber: '',
  emailAddress: '',
  patients: [],
  notes: [],
  patientPortalUserRequestId: 0,
  createdOnUtc: new Date(0).toISOString(),
  patientDisplayName: '',
  dateOfBirth: '1970-01-01',
  medicalRecordNumber: null,
  lastFiveSocialSecurityNumber: null,
  attempts: [],
};

@Component({
  selector: 'app-linking-request-detail',
  standalone: true,
  imports: [
    RouterLink,
    IconComponent,
    ProgressSpinnerComponent,
    ConfirmRequestModal,
    DenyRequestModal,
    AdjustSearch,
    RequestStatusCard,
  ],
  templateUrl: './linking-request-detail.html',
  styleUrl: './linking-request-detail.scss',
})
export class LinkingRequestDetail implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly requestsService = inject(PatientPortalUserRequestsService);
  private readonly notesService = inject(PatientPortalUserRequestNotesService);
  private readonly searchStateService = inject(PatientRecordsSearchStateService);

  readonly requestId = Number(this.route.snapshot.paramMap.get('requestId') ?? 0);

  readonly isLoading = signal(true);
  readonly loadError = signal<string | null>(null);
  readonly notFound = signal(false);
  readonly request = signal<RequestDetail>(EMPTY_REQUEST_DETAIL);
  /**
   * The chosen record's `patientId`, not its position in `searchResults()` —
   * that array is wholesale replaced by adjusted searches and by the Search
   * Patient Records handoff, so an index would silently point at a different
   * person if any writer ever forgot to reset it.
   */
  readonly selectedPatientId = signal<number | null>(null);
  readonly noteText = signal('');
  readonly isSubmittingNote = signal(false);
  readonly showNoteError = signal(false);
  readonly contactNoteText = signal('');
  readonly isLoggingContact = signal(false);
  readonly showContactNoteError = signal(false);
  readonly isSearching = signal(false);
  readonly isAssigning = signal(false);
  /**
   * Inline error for the adjusted patient search. Kept separate from
   * `assignmentError` so a failed search doesn't surface next to the Assignment
   * and Contact panels, which are elsewhere on the screen.
   */
  readonly searchError = signal<string | null>(null);
  /**
   * Inline save errors, one per composer/panel so the message lands where the
   * staff member acted rather than in all three at once. Without these the
   * failures were `console.error`-only: the audit note silently wasn't
   * persisted and the staff member saw nothing.
   */
  readonly noteError = signal<string | null>(null);
  readonly contactSaveError = signal<string | null>(null);
  readonly assignmentError = signal<string | null>(null);
  readonly activeModal = signal<ActiveModal>(null);

  /** The Search Patient Records hand-off is in flight. See {@link goToSearchRecords}. */
  readonly isOpeningSearch = signal(false);
  readonly toastMessage = signal<string | null>(null);
  readonly toastVariant = signal<'success' | 'error'>('success');
  readonly adjustSearchOpen = signal(false);
  readonly savedAdjustCriteria = signal<AdjustSearchCriteria | null>(null);
  private readonly adjustedSearchEvents = signal<TimelineEvent[]>([]);
  /** Unique-per-instance suffix for adjusted-search timeline track keys. */
  private nextAdjustedSearchEventId = 0;
  /**
   * Match count from the stored auto-search, captured once on load — the
   * patients array itself is overwritten by adjusted searches and the search
   * screen handoff, and the historical timeline entry must not follow it.
   */
  private readonly autoSearchCount = signal(0);
  private readonly resultsSource = signal<ResultsSource>('auto');
  /**
   * Server-side total for the search behind the displayed records. `null` means
   * unknown — the stored auto-search arrives inside the request detail, which
   * carries no total — and suppresses the truncation notice rather than guessing.
   */
  private readonly resultsTotalCount = signal<number | null>(null);

  // Staff-visible status shown in the header and Submitted Information badges.
  // The label/kind map is the shared one the queues and dashboard use
  // (shared/utils/status-badge), so the same request can't read under a
  // different label here; unknown or missing ids read as Unknown.
  private readonly statusBadge = computed(() =>
    getStatusBadge(this.request().workflowStatusTypeId),
  );
  readonly currentStatusLabel = computed(() => this.statusBadge().label);
  // Modifier for the global `.badge--*` classes, keyed off the semantic kind
  // (not the display label) so a copy change can't silently drop the styling.
  readonly statusBadgeClass = computed(() => `badge--${this.statusBadge().kind}`);
  readonly searchResults = computed(() => this.request().patients);
  readonly patientName = computed(() => this.request().patientDisplayName ?? '');
  /**
   * The submitted name for every place this screen *displays* it — the
   * breadcrumb crumb, the `<h1>`, the "Full Name" field, and the deny modal's
   * identity subtitle. An absent name reads as the shared `NOT_AVAILABLE` rather
   * than rendering blank: an empty heading is an axe `empty-heading` failure,
   * and a blank PHI value leaves staff unable to tell "no name was submitted"
   * from a rendering glitch — on the screen they read before linking or denying.
   *
   * `patientName` stays raw for the mismatch predicate: "Not available" must
   * never be compared against a patient record's name.
   */
  readonly patientNameLabel = computed(() => this.patientName() || NOT_AVAILABLE);
  private readonly submittedNameParts = computed(() =>
    splitDisplayName(this.request().patientDisplayName),
  );
  readonly adjustFirstName = computed(() => this.submittedNameParts().firstName);
  readonly adjustLastName = computed(() => this.submittedNameParts().lastName);
  readonly attempts = computed(() => this.request().attempts ?? []);
  // More than one recorded verification attempt marks the request as a
  // possible unauthorized-access attempt (security-alert treatment).
  readonly hasSecurityAlert = computed(() => this.attempts().length > 1);
  readonly submittedHeader = computed(() => {
    const submittedAt = this.request().createdOnUtc;
    const base = `Submitted ${formatLocalDate(submittedAt)} at ${formatLocalTime(submittedAt)}`;
    return this.hasSecurityAlert() ? `${base} · ${this.attempts().length} attempts` : base;
  });
  readonly dateOfBirthLabel = computed(() => formatDob(this.request().dateOfBirth));
  readonly phoneNumberLabel = computed(() => formatPhoneNumber(this.request().phoneNumber, ''));
  // This queue only holds self-linking requests (caregiver/proxy requests live
  // in their own queue), so the account type is a constant for now.
  readonly accountTypeLabel = 'Patient (Self)';
  // Identifier precedence and masking come from identifier.utils — the single
  // source of truth shared with the Confirm & Link modal.
  private readonly identifierParts = computed(() => getIdentifierParts(this.request()));
  readonly identifierTypeLabel = computed(() => this.identifierParts().typeLabel);
  readonly identifierFieldLabel = computed(() => this.identifierParts().fieldLabel);
  readonly isSsnIdentifier = computed(() => this.identifierParts().isMasked);
  readonly identifierValue = computed(() => this.identifierParts().value);
  readonly identifierAriaLabel = computed(() => getIdentifierAriaLabel(this.request()));
  /**
   * True when the server matched more records than are displayed. Searches
   * request a fixed PAGE_SIZE with no paging UI, so without this the card would
   * report a truncated page as the complete match set — and staff deny requests
   * that appear to have no match.
   */
  readonly isTruncated = computed(() => {
    const total = this.resultsTotalCount();
    return total !== null && total > this.searchResults().length;
  });
  readonly searchSummary = computed(() => {
    const count = this.searchResults().length;
    const results = this.isTruncated()
      ? `showing ${count} of ${this.resultsTotalCount()}`
      : `${count} result${count === 1 ? '' : 's'}`;
    switch (this.resultsSource()) {
      case 'adjusted':
        return `Adjusted search · ${results}`;
      case 'handoff':
        return `From Search Patient Records · ${results}`;
      default:
        return `Auto-searched on submission · ${results}`;
    }
  });
  readonly hasSearchResults = computed(() => this.searchResults().length > 0);
  /**
   * "No match" is the documented trigger for denying a request, so the empty
   * state has to name the search that actually came back empty rather than
   * always blaming the auto-search. `handoff` gets no branch of its own: it is
   * only set for a non-empty carried-back page, so it can't reach this message.
   */
  readonly searchEmptyStateMessage = computed(() =>
    this.resultsSource() === 'adjusted'
      ? 'No patient records matched the adjusted criteria. Use Adjust Search to broaden them, or deny the request if the patient’s identity cannot be verified.'
      : 'No patient records matched the submitted details. Use Search Patient Records to search with different criteria, or deny the request if the patient’s identity cannot be verified.',
  );
  readonly selectedPatient = computed(() => {
    const patientId = this.selectedPatientId();
    if (patientId === null) {
      return null;
    }
    return this.searchResults().find((patient) => patient.patientId === patientId) ?? null;
  });
  readonly timeline = computed(() =>
    [
      ...buildRequestTimeline(this.request(), this.autoSearchCount()),
      ...this.adjustedSearchEvents(),
    ].sort((a, b) => a.sortTime - b.sortTime),
  );
  readonly attemptTimeline = computed(() => buildAttemptTimeline(this.attempts()));
  readonly isAssigned = computed(() => this.request().assignedToPatientPortalAdminUserId != null);
  readonly assignedToLabel = computed(
    () => this.request().assignedToDisplayName?.trim() || 'Unassigned',
  );
  readonly isAssignedToMe = computed(() => {
    const assignedToId = this.request().assignedToPatientPortalAdminUserId;
    const currentUserId = this.authService.currentUser()?.patientPortalAdminUserId;
    return assignedToId != null && assignedToId === currentUserId;
  });
  readonly actionsPanelTitle = computed(() =>
    this.hasSecurityAlert() ? 'Further Actions' : 'Actions',
  );
  readonly confirmActionDescription = computed(() => {
    if (!this.selectedPatient()) {
      return 'Select a patient record above first';
    }
    return this.hasSecurityAlert()
      ? 'After manual verification, link to patient record'
      : 'Match selected record to this portal account';
  });
  readonly denyActionTitle = computed(() =>
    this.hasSecurityAlert() ? 'Deny & Close' : 'Deny Request',
  );
  readonly denyActionDescription = computed(() =>
    this.hasSecurityAlert()
      ? 'Mark as fraudulent and permanently deny'
      : 'Close without linking - cannot verify identity',
  );

  readonly errorTitle = computed(() =>
    this.notFound() ? 'Request not found' : 'Unable to load request',
  );
  readonly errorIcon = computed<IconName>(() => (this.notFound() ? 'search' : 'warning'));

  private toastTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.destroyRef.onDestroy(() => {
      if (this.toastTimeout !== null) {
        clearTimeout(this.toastTimeout);
      }
    });
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (this.activeModal() !== null && event.key === 'Escape') {
      this.closeModal();
    }
  }

  ngOnInit(): void {
    if (!Number.isInteger(this.requestId) || this.requestId <= 0) {
      this.isLoading.set(false);
      this.notFound.set(true);
      this.loadError.set(
        'This request may have been removed, or the link you followed is no longer valid.',
      );
      return;
    }

    // Take the handoff before the request load, not inside its success path:
    // consuming it is what clears it, and a failed load must not leave a set of
    // patient records sitting in the root-provided service until sign-out.
    const handoff = this.searchStateService.consumeHandoff(this.requestId);

    this.requestsService
      .getRequestById(this.requestId)
      .pipe(
        catchError((error) => {
          logHttpError('Failed to load request detail', error);
          const notFound = error?.status === 404;
          this.notFound.set(notFound);
          this.loadError.set(
            notFound
              ? 'This request may have been removed, or the link you followed is no longer valid.'
              : 'Something went wrong while loading this request. Please try again.',
          );
          return of(null);
        }),
        // Single owner for the spinner, matching the search screen: two callers
        // clearing one signal is how a screen ends up stranded on it.
        finalize(() => {
          this.isLoading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((request) => {
        if (!request) {
          return;
        }

        // Snapshot the stored auto-search count before anything can overwrite
        // the patients array — the timeline's historical entry reads this.
        this.autoSearchCount.set(request.patients.length);

        // Results carried back from the Search Patient Records screen replace
        // the stored auto-search results, with the reviewed record selected.
        if (handoff && handoff.patients.length > 0) {
          this.request.set({ ...request, patients: handoff.patients });
          this.resultsSource.set('handoff');
          this.resultsTotalCount.set(handoff.totalRecordCount);
          // Fall back to the first record if the reviewed one isn't in the
          // carried-back page, so the card is never left with nothing selected.
          const reviewed = handoff.patients.some(
            (patient) => patient.patientId === handoff.selectedPatientId,
          );
          this.selectedPatientId.set(
            reviewed ? handoff.selectedPatientId : handoff.patients[0].patientId,
          );

          // Select & Review opens the review itself, rather than landing staff on the detail screen
          // to find the button again. Only when the reviewed record actually came back, though: on
          // the fallback above the modal would open onto a record nobody chose.
          if (handoff.openReview && reviewed) {
            this.activeModal.set('confirm-link');
          }
        } else {
          this.request.set(request);
          // The stored auto-search comes back inside the request detail, which
          // carries no total — leave the count unknown rather than implying the
          // list is complete.
          this.resultsTotalCount.set(null);
          this.selectedPatientId.set(request.patients[0]?.patientId ?? null);
        }

        this.loadError.set(null);
        this.notFound.set(false);
      });
  }

  selectSearchResult(patientId: number): void {
    this.selectedPatientId.set(patientId);
  }

  updateNoteText(value: string): void {
    this.noteText.set(value);
    if (value.trim()) {
      this.showNoteError.set(false);
    }
  }

  // Mirrors the Log Contact behavior — an empty Add Note click prompts for a
  // note instead of silently doing nothing.
  addNote(): void {
    if (!this.noteText().trim()) {
      this.showNoteError.set(true);
      return;
    }

    this.showNoteError.set(false);
    this.submitNote(this.noteText, this.isSubmittingNote, this.noteError);
  }

  updateContactNoteText(value: string): void {
    this.contactNoteText.set(value);
    if (value.trim()) {
      this.showContactNoteError.set(false);
    }
  }

  cancelContactNote(): void {
    this.contactNoteText.set('');
    this.showContactNoteError.set(false);
    this.contactSaveError.set(null);
  }

  // AC11 — an empty Log Contact click prompts for a note instead of silently
  // doing nothing; a successful save confirms with a toast.
  logContact(): void {
    if (!this.contactNoteText().trim()) {
      this.showContactNoteError.set(true);
      return;
    }

    this.showContactNoteError.set(false);
    this.submitNote(
      this.contactNoteText,
      this.isLoggingContact,
      this.contactSaveError,
      'Contact logged and added to the activity timeline.',
    );
  }

  // Shared save path for the Activity & Notes composer and the
  // "Contact Patient & Verify" panel — both persist through the notes API and
  // surface in the activity timeline.
  private submitNote(
    textSignal: WritableSignal<string>,
    busySignal: WritableSignal<boolean>,
    errorSignal: WritableSignal<string | null>,
    successToast?: string,
  ): void {
    const note = textSignal().trim();
    if (!note || busySignal()) {
      return;
    }

    // Notes are audit records — never attribute one to a made-up admin id.
    const adminUserId = this.currentAdminUserId();
    if (adminUserId == null) {
      console.error('Cannot save note: no signed-in admin user.');
      errorSignal.set(SESSION_ERROR);
      return;
    }

    errorSignal.set(null);
    busySignal.set(true);

    this.notesService
      .createNote({
        patientPortalUserRequestId: this.requestId,
        createdByPatientPortalAdminUserId: adminUserId,
        note,
      })
      .pipe(
        // finalize (not the handlers) resets the busy flag so a throw inside
        // next can never leave the button disabled for good.
        finalize(() => {
          busySignal.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (createdNote) => {
          this.request.update((request) => ({
            ...request,
            notes: [...request.notes, createdNote],
          }));
          textSignal.set('');
          if (successToast) {
            this.showToast(successToast);
          }
        },
        error: (error) => {
          logHttpError('Failed to add note', error);
          // The note is an audit record — never let a failed save look like a
          // successful one. The text stays in the box so it can be retried.
          errorSignal.set('Unable to save the note. Please try again.');
        },
      });
  }

  openModal(modal: Exclude<ActiveModal, null>): void {
    this.activeModal.set(modal);
  }

  closeModal(): void {
    this.activeModal.set(null);
  }

  openAdjustSearch(): void {
    this.adjustSearchOpen.set(true);
  }

  closeAdjustSearch(): void {
    this.adjustSearchOpen.set(false);
  }

  onAdjustSearchCancelled(criteria: AdjustSearchCriteria): void {
    this.savedAdjustCriteria.set(criteria);
    this.closeAdjustSearch();
  }

  handleSearchRun(criteria: AdjustSearchCriteria): void {
    // The search is audited against the admin user the API reads off the access
    // token, so there is no id for this screen to supply or check.
    this.searchError.set(null);
    this.savedAdjustCriteria.set(criteria);
    const searchRequest = buildPatientSearchRequest(this.requestId, criteria);

    this.adjustSearchOpen.set(false);
    this.isSearching.set(true);

    this.requestsService
      .searchPatients(searchRequest)
      .pipe(
        finalize(() => {
          this.isSearching.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result) => {
          const now = new Date();
          const user = this.authService.currentUser();
          const staffName = user ? `${user.firstName} ${user.lastName}`.trim() : 'staff';

          this.adjustedSearchEvents.update((events) => [
            ...events,
            {
              // A counter, not a timestamp — two searches completing in the
              // same millisecond must not produce duplicate track keys.
              id: `adjusted-${this.nextAdjustedSearchEventId++}`,
              variant: 'standard',
              kind: 'staff',
              icon: 'manage_search',
              description: `Adjusted search run by ${staffName}.`,
              sortTime: now.getTime(),
              timestamp: formatTimelineTimestamp(now),
            },
          ]);

          this.request.update((r) => ({ ...r, patients: result.patients }));
          this.resultsSource.set('adjusted');
          this.resultsTotalCount.set(result.totalRecordCount);
          this.selectedPatientId.set(result.patients[0]?.patientId ?? null);
        },
        error: (error) => {
          logHttpError('Failed to run adjusted patient search', error);
          // Leave the previous results in place; a failed search must not read
          // as "no matches", which is what leads staff to deny a request.
          this.searchError.set('Unable to run the search. Please try again.');
        },
      });
  }

  assignToMe(): void {
    if (this.isAssigning() || this.isAssignedToMe()) {
      return;
    }

    // Assignment is an audit record — don't route a request to a user we can't
    // name, and tell the staff member instead of silently doing nothing.
    const user = this.authService.currentUser();
    const adminUserId = user?.patientPortalAdminUserId;
    if (!user || adminUserId == null) {
      console.error('Cannot assign request: no signed-in admin user.');
      this.assignmentError.set(SESSION_ERROR);
      return;
    }

    const assigneeName = `${user.firstName} ${user.lastName}`.trim();
    this.assignmentError.set(null);
    this.isAssigning.set(true);

    this.requestsService
      .assignRequest(this.requestId, {
        assignToPatientPortalAdminUserId: adminUserId,
        version: this.request().version,
      })
      .pipe(
        // finalize (not the handlers) resets the busy flag so a throw inside
        // next can never leave the button disabled for good.
        finalize(() => {
          this.isAssigning.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => {
          this.request.update((request) => ({
            ...request,
            // If the envelope comes back empty, keep the version we sent
            // rather than corrupting the concurrency chain with undefined.
            version: response?.version ?? request.version,
            assignedToPatientPortalAdminUserId: adminUserId,
            assignedToDisplayName: assigneeName,
          }));
          this.showToast(`Request assigned to ${assigneeName}.`);
        },
        error: (error) => {
          logHttpError('Failed to assign request', error);
          this.assignmentError.set('Unable to assign the request. Please try again.');
        },
      });
  }

  // AC13 — clearing the assignment sends a null assignee id and reverts the
  // card to Unassigned.
  unassign(): void {
    if (this.isAssigning() || !this.isAssignedToMe()) {
      return;
    }

    this.assignmentError.set(null);
    this.isAssigning.set(true);

    this.requestsService
      .assignRequest(this.requestId, {
        assignToPatientPortalAdminUserId: null,
        version: this.request().version,
      })
      .pipe(
        finalize(() => {
          this.isAssigning.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => {
          this.request.update((request) => ({
            ...request,
            version: response?.version ?? request.version,
            assignedToPatientPortalAdminUserId: null,
            assignedToDisplayName: null,
          }));
          this.showToast('Request unassigned.');
        },
        error: (error) => {
          logHttpError('Failed to unassign request', error);
          this.assignmentError.set('Unable to unassign the request. Please try again.');
        },
      });
  }

  // AC6/AC7 — confirmation toast after the Request Status card persists the
  // new status through the SetStatus API. The refreshed concurrency version is
  // folded in so later mutations (assign, confirm, deny) don't send a stale one.
  onStatusUpdated(selection: RequestStatusSelection): void {
    this.request.update((request) => ({
      ...request,
      workflowStatusTypeId: selection.status,
      // A null version means the SetStatus envelope carried none — keep the
      // version we already hold rather than rolling it back to the one the card
      // sent, which a concurrent assign may already have superseded.
      version: selection.version ?? request.version,
    }));
    this.showToast(`Status updated to ${selection.label}.`);
  }

  /**
   * Opens the dedicated Search Patient Records screen, pre-filled from this request.
   *
   * The button holds a spinner from the click until the route activates. The search screen is a
   * sibling route, not a child, so this component is destroyed the moment it does — the spinner
   * covers the async `authGuard` run in between, the stretch where an unchanged button is a click
   * that appears not to have landed, which is how a staff member ends up pressing it twice.
   *
   * The flag is never cleared on success, deliberately: clearing it would only flash the original
   * label over a screen that is already leaving. A refused or failed navigation does clear it,
   * because then there is nothing leaving and the button has to be pressable again.
   */
  goToSearchRecords(): void {
    if (this.isOpeningSearch()) {
      return;
    }

    this.isOpeningSearch.set(true);
    void this.router.navigate(['/request-detail', this.requestId, 'search']).then(
      (navigated) => {
        if (!navigated) {
          this.isOpeningSearch.set(false);
        }
      },
      () => this.isOpeningSearch.set(false),
    );
  }

  private currentAdminUserId(): number | null {
    return this.authService.currentUser()?.patientPortalAdminUserId ?? null;
  }

  private showToast(message: string, variant: 'success' | 'error' = 'success'): void {
    this.toastMessage.set(message);
    this.toastVariant.set(variant);

    if (this.toastTimeout !== null) {
      clearTimeout(this.toastTimeout);
    }
    this.toastTimeout = setTimeout(() => {
      this.toastMessage.set(null);
    }, 4000);
  }

  onRequestDenied(): void {
    this.router.navigate(['/requests']);
  }

  onRequestConfirmed(): void {
    this.router.navigate(['/requests']);
  }

  isSearchResultSelected(patientId: number): boolean {
    return this.selectedPatientId() === patientId;
  }

  getSearchResultDateOfBirth(patient: RequestDetailPatient): string {
    return formatDobNumeric(patient.dateOfBirth);
  }

  // AC16 — discrepancies between a matched record and the submitted info are
  // highlighted on the result card. Both predicates come from
  // identity-match.utils, which is also what the Confirm & Link modal gates its
  // override on, so the chip here can't disagree with the modal there.
  isResultNameMismatch(patient: RequestDetailPatient): boolean {
    return isNameMismatch(this.patientName(), patient.displayName);
  }

  isResultDobMismatch(patient: RequestDetailPatient): boolean {
    return isDateOfBirthMismatch(this.request().dateOfBirth, patient.dateOfBirth);
  }

  /**
   * Chipped on the same terms as the two above, and for the same reason: the Confirm & Link modal
   * now gates its override on a submitted MRN the record can't corroborate, so a card that stayed
   * silent about it would send staff into a justification they were given no warning of.
   */
  isResultMrnMismatch(patient: RequestDetailPatient): boolean {
    return isMedicalRecordNumberMismatch(
      this.request().medicalRecordNumber,
      patient.medicalRecordNumber,
    );
  }

  getResultInitials(patient: RequestDetailPatient): string {
    return getPatientInitials(patient.displayName);
  }

  getSearchResultAddress(patient: RequestDetailPatient): string {
    return formatPatientAddress(patient.address);
  }

  getSearchResultPhoneNumber(patient: RequestDetailPatient): string {
    return formatPatientPhone(patient.phoneNumber);
  }

  getSearchResultPrimaryCareProvider(patient: RequestDetailPatient): string {
    return formatPatientPcp(patient.primaryCareProvider);
  }

  getSearchResultMedicalRecordNumber(patient: RequestDetailPatient): string {
    return formatPatientMrn(patient.medicalRecordNumber);
  }
}
